// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';

export function isDisplayDictionaryModel(source: any): source is IDisplayDictionary {
    // Method Implementation: simple
    return source.modelName === 'DisplayDictionary';
}

export function isCultureModel(source: any): source is ICulture {
    // Method Implementation: simple
    return source.modelName === 'Culture';
}

export function isTranslationModel(source: any): source is ITranslation {
    // Method Implementation: simple
    return source.modelName === 'Translation';
}

export interface DisplayDictionaryZodSchemaShape {
    cultures?: CultureZodSchemaShape[];
}

export interface CultureZodSchemaShape {
    name?: string;

    baseCulture?: string;

    translations?: TranslationZodSchemaShape[];
}

export interface TranslationZodSchemaShape {
    key?: string;

    displayValue?: string;
}

// Artifact Implementation: artifacts/zodSchemaType
export const DisplayDictionaryZodSchema: Zod.ZodSchema<DisplayDictionaryZodSchemaShape> = Zod.lazy(() => Zod.object({
    cultures: Zod.array(CultureZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const CultureZodSchema: Zod.ZodSchema<CultureZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    baseCulture: Zod.string().optional(),
    translations: Zod.array(TranslationZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const TranslationZodSchema: Zod.ZodSchema<TranslationZodSchemaShape> = Zod.lazy(() => Zod.object({
    key: Zod.string().optional(),
    displayValue: Zod.string().optional()
}).nonstrict());

export interface IDisplayDictionary extends Wellspring.IModel {
    readonly modelName: string;

    readonly cultures: Culture[];

    readonly isCulturesDefined: boolean;
}

export interface ICulture extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly baseCulture: string;

    readonly translations: Translation[];

    readonly isNameDefined: boolean;

    readonly isBaseCultureDefined: boolean;

    readonly isTranslationsDefined: boolean;
}

export interface ITranslation extends Wellspring.IModel {
    readonly modelName: string;

    readonly key: string;

    readonly displayValue: string;

    readonly isKeyDefined: boolean;

    readonly isDisplayValueDefined: boolean;
}

export class DisplayDictionary implements IDisplayDictionary {
    public static readonly modelName: string = 'DisplayDictionary';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'cultures' ];

    public get modelName(): string {
        // Method Implementation: simple
        return DisplayDictionary.modelName;
    }

    private _cultures: Culture[];

    public get cultures(): Culture[] {
        // Method Implementation: simple
        return this._cultures;
    }

    public set cultures(value: Culture[]) {
        // Method Implementation: simple
        this._cultures = value;
        this._isCulturesDefined = true;
    }

    private _isCulturesDefined: boolean;

    public get isCulturesDefined(): boolean {
        // Method Implementation: simple
        return this._isCulturesDefined;
    }

    public constructor();
    public constructor(source: IDisplayDictionary);
    public constructor(source: Partial<Pick<DisplayDictionary, 'cultures'>>);
    public constructor(source?: IDisplayDictionary | Partial<Pick<DisplayDictionary, 'cultures'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isDisplayDictionaryModel(source)) {
                if (source.isCulturesDefined) {
                    this._cultures = source.cultures;
                    this._isCulturesDefined = true;
                }
                else {
                    this._cultures = [];
                    this._isCulturesDefined = true;
                }
            }
            else {
                if (source.cultures !== undefined) {
                    this._cultures = source.cultures;
                    this._isCulturesDefined = true;
                }
                else {
                    this._cultures = [];
                    this._isCulturesDefined = true;
                }
            }
        }
        else {
            this._cultures = [];
            this._isCulturesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return DisplayDictionary.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isCulturesDefined && this.cultures !== undefined)
            definedProperties.push('cultures');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'cultures':
                return this.cultures;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isCulturesDefined && this.cultures !== undefined) {
            if (this.cultures !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('cultures', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.cultures.length));
                for (let i = 0; i < this.cultures.length; i++) {
                    const item = this.cultures[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('cultures', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'cultures':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.cultures = [];
                
                    let item: Culture = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Culture.modelName) {
                                item = new Culture();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Culture.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'cultures' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'cultures'`);
                        }
                
                        this.cultures.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'cultures' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'cultures'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // cultures
        if (this.isCulturesDefined && this.cultures !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.cultures === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.cultures.length);
            
                for (const item of this.cultures) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // cultures
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Culture;
            
                    this.cultures = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Culture();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.cultures.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): DisplayDictionary {
        // Method Implementation: simple
        const schema = DisplayDictionaryZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: DisplayDictionaryZodSchemaShape): DisplayDictionary {
        // Method Implementation: parseZodSchema
        return new DisplayDictionary({
            cultures: schema.cultures?.map(i => Culture.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            cultures: this.isCulturesDefined ? (this.cultures?.map(item => item)) : undefined,
        };
    }

    public clone(): DisplayDictionary {
        // Method Implementation: clone
        return new DisplayDictionary({
            cultures: this.isCulturesDefined ? this.cultures?.map(i => i.clone()) : undefined
        });
    }
}

export class Culture implements ICulture {
    public static readonly modelName: string = 'Culture';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'baseCulture', 'translations' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Culture.modelName;
    }

    private _name: string;

    private _baseCulture: string;

    private _translations: Translation[];

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get baseCulture(): string {
        // Method Implementation: simple
        return this._baseCulture;
    }

    public get translations(): Translation[] {
        // Method Implementation: simple
        return this._translations;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set baseCulture(value: string) {
        // Method Implementation: simple
        this._baseCulture = value;
        this._isBaseCultureDefined = true;
    }

    public set translations(value: Translation[]) {
        // Method Implementation: simple
        this._translations = value;
        this._isTranslationsDefined = true;
    }

    private _isNameDefined: boolean;

    private _isBaseCultureDefined: boolean;

    private _isTranslationsDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isBaseCultureDefined(): boolean {
        // Method Implementation: simple
        return this._isBaseCultureDefined;
    }

    public get isTranslationsDefined(): boolean {
        // Method Implementation: simple
        return this._isTranslationsDefined;
    }

    public constructor();
    public constructor(source: ICulture);
    public constructor(source: Partial<Pick<Culture, 'name' | 'baseCulture' | 'translations'>>);
    public constructor(source?: ICulture | Partial<Pick<Culture, 'name' | 'baseCulture' | 'translations'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isCultureModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isBaseCultureDefined) {
                    this._baseCulture = source.baseCulture;
                    this._isBaseCultureDefined = true;
                }
                else {
                    this._baseCulture = '';
                    this._isBaseCultureDefined = false;
                }
                
                if (source.isTranslationsDefined) {
                    this._translations = source.translations;
                    this._isTranslationsDefined = true;
                }
                else {
                    this._translations = [];
                    this._isTranslationsDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.baseCulture !== undefined) {
                    this._baseCulture = source.baseCulture;
                    this._isBaseCultureDefined = true;
                }
                else {
                    this._baseCulture = '';
                    this._isBaseCultureDefined = false;
                }
                
                if (source.translations !== undefined) {
                    this._translations = source.translations;
                    this._isTranslationsDefined = true;
                }
                else {
                    this._translations = [];
                    this._isTranslationsDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._baseCulture = '';
            this._isBaseCultureDefined = false;
        
            this._translations = [];
            this._isTranslationsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Culture.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isBaseCultureDefined && this.baseCulture !== undefined)
            definedProperties.push('baseCulture');
        if (this.isTranslationsDefined && this.translations !== undefined)
            definedProperties.push('translations');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'baseCulture':
                return this.baseCulture;
            case 'translations':
                return this.translations;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isBaseCultureDefined && this.baseCulture !== undefined) {
            if (this.baseCulture !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('baseCulture', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.baseCulture);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('baseCulture', 'null'));
            }
        }
        
        if (this.isTranslationsDefined && this.translations !== undefined) {
            if (this.translations !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('translations', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.translations.length));
                for (let i = 0; i < this.translations.length; i++) {
                    const item = this.translations[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('translations', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'baseCulture':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.baseCulture = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'baseCulture' cannot be null`);
                    this.baseCulture = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'baseCulture'`);
                }
        
                return true;
            case 'translations':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.translations = [];
                
                    let item: Translation = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Translation.modelName) {
                                item = new Translation();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Translation.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'translations' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'translations'`);
                        }
                
                        this.translations.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'translations' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'translations'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // baseCulture
        if (this.isBaseCultureDefined && this.baseCulture !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.baseCulture === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.baseCulture);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // translations
        if (this.isTranslationsDefined && this.translations !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.translations === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.translations.length);
            
                for (const item of this.translations) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // baseCulture
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.baseCulture = <any>null;
                    break;
                case 1:
                    this.baseCulture = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // translations
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Translation;
            
                    this.translations = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Translation();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.translations.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Culture {
        // Method Implementation: simple
        const schema = CultureZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: CultureZodSchemaShape): Culture {
        // Method Implementation: parseZodSchema
        return new Culture({
            name: schema.name,
            baseCulture: schema.baseCulture,
            translations: schema.translations?.map(i => Translation.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            baseCulture: this.isBaseCultureDefined ? ((this.baseCulture === null ? undefined : this.baseCulture)) : undefined,
            translations: this.isTranslationsDefined ? (this.translations?.map(item => item)) : undefined,
        };
    }

    public clone(): Culture {
        // Method Implementation: clone
        return new Culture({
            name: this.isNameDefined ? this.name : undefined,
            baseCulture: this.isBaseCultureDefined ? this.baseCulture : undefined,
            translations: this.isTranslationsDefined ? this.translations?.map(i => i.clone()) : undefined
        });
    }
}

export class Translation implements ITranslation {
    public static readonly modelName: string = 'Translation';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'key', 'displayValue' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Translation.modelName;
    }

    private _key: string;

    private _displayValue: string;

    public get key(): string {
        // Method Implementation: simple
        return this._key;
    }

    public get displayValue(): string {
        // Method Implementation: simple
        return this._displayValue;
    }

    public set key(value: string) {
        // Method Implementation: simple
        this._key = value;
        this._isKeyDefined = true;
    }

    public set displayValue(value: string) {
        // Method Implementation: simple
        this._displayValue = value;
        this._isDisplayValueDefined = true;
    }

    private _isKeyDefined: boolean;

    private _isDisplayValueDefined: boolean;

    public get isKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isKeyDefined;
    }

    public get isDisplayValueDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayValueDefined;
    }

    public constructor();
    public constructor(source: ITranslation);
    public constructor(source: Partial<Pick<Translation, 'key' | 'displayValue'>>);
    public constructor(source?: ITranslation | Partial<Pick<Translation, 'key' | 'displayValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isTranslationModel(source)) {
                if (source.isKeyDefined) {
                    this._key = source.key;
                    this._isKeyDefined = true;
                }
                else {
                    this._key = '';
                    this._isKeyDefined = false;
                }
                
                if (source.isDisplayValueDefined) {
                    this._displayValue = source.displayValue;
                    this._isDisplayValueDefined = true;
                }
                else {
                    this._displayValue = '';
                    this._isDisplayValueDefined = false;
                }
            }
            else {
                if (source.key !== undefined) {
                    this._key = source.key;
                    this._isKeyDefined = true;
                }
                else {
                    this._key = '';
                    this._isKeyDefined = false;
                }
                
                if (source.displayValue !== undefined) {
                    this._displayValue = source.displayValue;
                    this._isDisplayValueDefined = true;
                }
                else {
                    this._displayValue = '';
                    this._isDisplayValueDefined = false;
                }
            }
        }
        else {
            this._key = '';
            this._isKeyDefined = false;
        
            this._displayValue = '';
            this._isDisplayValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Translation.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isKeyDefined && this.key !== undefined)
            definedProperties.push('key');
        if (this.isDisplayValueDefined && this.displayValue !== undefined)
            definedProperties.push('displayValue');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'key':
                return this.key;
            case 'displayValue':
                return this.displayValue;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isKeyDefined && this.key !== undefined) {
            if (this.key !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('key', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.key);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('key', 'null'));
            }
        }
        
        if (this.isDisplayValueDefined && this.displayValue !== undefined) {
            if (this.displayValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayValue', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayValue', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'key':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.key = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'key' cannot be null`);
                    this.key = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'key'`);
                }
        
                return true;
            case 'displayValue':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayValue = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayValue' cannot be null`);
                    this.displayValue = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayValue'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // key
        if (this.isKeyDefined && this.key !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.key === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.key);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayValue
        if (this.isDisplayValueDefined && this.displayValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayValue === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayValue);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // key
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.key = <any>null;
                    break;
                case 1:
                    this.key = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayValue
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayValue = <any>null;
                    break;
                case 1:
                    this.displayValue = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Translation {
        // Method Implementation: simple
        const schema = TranslationZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: TranslationZodSchemaShape): Translation {
        // Method Implementation: parseZodSchema
        return new Translation({
            key: schema.key,
            displayValue: schema.displayValue
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            key: this.isKeyDefined ? ((this.key === null ? undefined : this.key)) : undefined,
            displayValue: this.isDisplayValueDefined ? ((this.displayValue === null ? undefined : this.displayValue)) : undefined,
        };
    }

    public clone(): Translation {
        // Method Implementation: clone
        return new Translation({
            key: this.isKeyDefined ? this.key : undefined,
            displayValue: this.isDisplayValueDefined ? this.displayValue : undefined
        });
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'DisplayDictionary':
                return new DisplayDictionary();
            case 'Culture':
                return new Culture();
            case 'Translation':
                return new Translation();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'DisplayDictionary':
                return new DisplayDictionary(source);
            case 'Culture':
                return new Culture(source);
            case 'Translation':
                return new Translation(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'DisplayDictionary':
                return DisplayDictionary.parse(source);
            case 'Culture':
                return Culture.parse(source);
            case 'Translation':
                return Translation.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
